import { useState } from 'react';
import { ButtonPopup, Input, Select } from '../ui'; // Componentes UI reutilizables
import { PopupProps } from '@/types'; // Tipos de props para los popups
import { PopupStilosCss } from '../styles'; // Estilos CSS específicos para el popup

// ✅ Configuración centralizada de redes sociales
import { SocialPlatform, SOCIAL_PLATFORMS, PLATFORM_URLS } from '../utils/SocialConfig';

/**
 * PopupRedesSociales
 * 
 * Modal para añadir una red social con nombre de usuario.
 * Permite seleccionar una plataforma y generar automáticamente la URL.
 * 
 * Props extendidas de PopupProps:
 * - onClose: función para cerrar el popup
 * - onAddSocial: callback para añadir la red social al estado padre
 * - platformLogos: objeto con rutas de iconos por plataforma
 * - className: clases CSS adicionales opcionales
 */
const PopupRedesSociales: React.FC<PopupProps> = ({ onClose, onAddSocial, platformLogos, className }) => {
    const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | ''>(''); // Plataforma seleccionada
    const [username, setUsername] = useState<string>(''); // Nombre de usuario de la red social

    // Función que valida y envía la nueva red social al padre
    const addSocialLink = () => {
        if (!selectedPlatform || !username) {
            alert('Por favor, selecciona una red social e introduce tu nombre de usuario.');
            return;
        }

        // Construye la URL completa concatenando base + username
        const url = `${PLATFORM_URLS[selectedPlatform]}${username}`;
        const iconPath = platformLogos?.[selectedPlatform]; // Icono opcional

        // Llama al callback con los datos de la nueva red social
        onAddSocial?.({ nombre: selectedPlatform, enlace: url, iconPath });

        // Resetea el estado y cierra el popup
        setSelectedPlatform('');
        setUsername('');
        onClose?.();
    };

    return (
        // Contenedor que cubre toda la pantalla y centra el contenido
        <div className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ${PopupStilosCss.popupRedesSociales}`}>
            {/* Contenedor del contenido del popup */}
            <div className={`${className} space-y-4 bg-zinc-400 shadow-xl rounded-md p-8 relative overflow-auto ${PopupStilosCss.popupRedesSociales}`}>
                <h2 className="text-xl font-bold">Añadir Redes Sociales</h2>

                {/* Inputs para seleccionar la red social y el nombre de usuario */}
                <div className="space-y-4">
                    <Select
                        value={selectedPlatform}
                        onChange={(e) => setSelectedPlatform(e.target.value as SocialPlatform)}
                        options={SOCIAL_PLATFORMS.map(platform => ({ value: platform, label: platform }))}
                        placeholder="Red social"
                        className="dark:bg-white dark:text-black"
                    />
                    <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Nombre de usuario"
                        id="username"
                        type="text"
                        className="px-3 py-2 w-10/12 dark:bg-white dark:text-black text-sm"
                    />
                </div>

                {/* Botones para Añadir o Cancelar */}
                <div className="flex space-x-2 text-center">
                    <ButtonPopup
                        onClick={addSocialLink}
                        isAddButton={true}
                        className="h-8 w-3/6 transition-colors items-center bg-zinc-600 shadow-md shadow-zinc-700 rounded-md duration-400 border hover:border-green-600 active:font-extrabold active:bg-white active:text-black text-white">
                        Añadir
                    </ButtonPopup>
                    <ButtonPopup
                        onClick={onClose}
                        className="h-8 w-3/6 transition-colors items-center bg-zinc-600 shadow-md shadow-zinc-700 rounded-md duration-400 border hover:border-red-600 active:bg-white active:text-black text-white active:font-extrabold">
                        Cancelar
                    </ButtonPopup>
                </div>
            </div>
        </div>
    );
};

export default PopupRedesSociales;
